<template>
    <div class="inline-block">
        <span class="inline-block"><p v-html="highlightedText" /></span>
        <button
            @click="speak()"
            type="button"
            v-if="isSupported"
            class="ml-2 text-indigo-700 bg-indigo-100 hover:bg-indigo-200 inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
            <IconVolume class="w-4 h-4" />
        </button>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useVoices } from '@/composables/useVoices.js';
import { IconVolume } from '@tabler/icons-vue';
import { useSpeechRate } from '@/composables/useSpeechRate.js';
import { useSpeechSynthesis } from '@vueuse/core';

const { isSupported } = useSpeechSynthesis();
const { rate } = useSpeechRate();

const { getVoice } = useVoices();
// Define props
const props = defineProps({
    text: {
        type: String,
        required: true,
    },
    lang: {
        type: String,
        default: 'es',
    },
});

const text = ref(props.text);

let words = ref(text.value.split(' '));

const position = ref(-1);
const locales = (l) => {
    const preferences = {
        en: ['en'],
        es: ['es'],
        ca: ['ca', 'es'],
    };
    return preferences[l];
};

watch(
    () => props.text,
    (newText) => {
        text.value = newText;
        words.value = text.value.split(' ');
        position.value = -1;
    },
);

const highlightedText = computed(() => {
    let highlighted = '';
    for (let i = 0; i < words.value.length; i++) {
        const word = words.value[i];
        const highlightedWord = position.value === i ? `<span class="highlighted-word">${word}</span>` : word;
        highlighted += `${highlightedWord} `;
    }
    return highlighted.trim();
});

function speak() {
    const speech = new SpeechSynthesisUtterance();
    speech.voice = getVoice(locales(props.lang));
    speech.rate = rate.value;
    position.value = 0;
    speech.text = text.value.replace(/<[^>]*>/g, '');

    speechSynthesis.cancel();
    speechSynthesis.speak(speech);

    speech.onboundary = () => {
        position.value++;
    };
}
</script>

<style>
.highlighted-word {
    background-color: #47b5f55e;
}
</style>
